/**
 * Course-with-bundles fetch lifecycle for the checkout orchestrator.
 *
 * The buyer's identity (apiConfig.buyerEmail) determines which tickets the
 * API reveals (tag-gated / members-only), and it can resolve or change after
 * mount — so ensureLoaded() re-fetches whenever the email changes, silently:
 * the loading gate only guards the very first load, so in-progress checkout
 * state survives a login. Responses are sequence-guarded (a superseded
 * response never overwrites a newer one), but a superseded success is still
 * applied provisionally while nothing is on screen — better an anonymous
 * ticket list than a spinner or error while the identity fetch settles.
 * Failures surface only when there is no data to keep showing, and a later
 * success clears any surfaced error.
 */

import { fetchCourseWithBundles, fetchParticipantFields } from "./api";
import type { CheckoutApiConfig, CourseWithBundlesResponse } from "./types";
import type { CheckoutParticipantField } from "../../components/checkout/participants-step";

export type CourseDataLoaderCallbacks = {
  onLoadingChange: (isLoading: boolean) => void;
  onData: (data: CourseWithBundlesResponse) => void;
  onParticipantFields: (fields: CheckoutParticipantField[]) => void;
  /** A message surfaces a failure; null clears a previously surfaced one. */
  onErrorMessage: (message: string | null) => void;
};

export function createCourseDataLoader(
  listingId: string,
  callbacks: CourseDataLoaderCallbacks,
) {
  let seq = 0;
  let started = false;
  let startedForBuyerEmail: string | null = null;
  let hasData = false;
  let participantFieldsStarted = false;

  function applyData(data: CourseWithBundlesResponse) {
    hasData = true;
    callbacks.onData(data);
    callbacks.onErrorMessage(null);
    callbacks.onLoadingChange(false);
  }

  async function load(apiConfig: CheckoutApiConfig, isFirstLoad: boolean) {
    const mySeq = ++seq;

    if (isFirstLoad) {
      callbacks.onLoadingChange(true);
      callbacks.onErrorMessage(null);
    }

    try {
      const data = await fetchCourseWithBundles(listingId, apiConfig);
      // Attendee field definitions are identity-independent: exactly one
      // fetch ever, made by whichever request first gets this far.
      // fetchParticipantFields never throws (fails open to [] ⇒ legacy
      // guest questions).
      if (!participantFieldsStarted && data.school?.participantsEnabled) {
        participantFieldsStarted = true;
        callbacks.onParticipantFields(
          await fetchParticipantFields(data.school._id, listingId, apiConfig),
        );
      }
      if (mySeq !== seq) {
        // Superseded by a newer fetch — apply provisionally when nothing is
        // shown yet; the newer response overwrites (or keeps) it on arrival.
        if (!hasData) applyData(data);
        return;
      }
      applyData(data);
    } catch (err) {
      if (mySeq !== seq) return; // a newer fetch owns the outcome now
      if (hasData) return; // failed identity refresh: keep the current data
      const message =
        err instanceof Error ? err.message : "Failed to load course data";
      callbacks.onErrorMessage(message);
      callbacks.onLoadingChange(false);
    }
  }

  return {
    /**
     * Fetch if never fetched, or if the buyer email changed since the last
     * fetch was started; otherwise a no-op — safe to call on every render.
     */
    ensureLoaded(apiConfig: CheckoutApiConfig) {
      const buyerEmail = apiConfig.buyerEmail ?? null;
      if (started && startedForBuyerEmail === buyerEmail) return;
      const isFirstLoad = !started;
      started = true;
      startedForBuyerEmail = buyerEmail;
      void load(apiConfig, isFirstLoad);
    },
  };
}
